/**
 * Response adapter for the unified opportunity-DOORS endpoint (FIR-6995).
 *
 * The door mirror of `adaptUnifiedItemForVertical` in `unifiedResponseAdapter.ts`,
 * but the door row shape is two-level where the opps row is one-level, so it
 * needs its own re-nesting:
 *
 *  - The unified door row emits the door's own attributes FLAT at the top level
 *    (`name`, `street_address`, `city`, `state`, `zipcode`, `country`, `phone`)
 *    plus that vertical's door SEGMENT columns flat (`place_url`, `reviews_avg`,
 *    `reviews_cnt`, `chain_doors_cnt`, restaurant `chain_name`/`avg_rating`/…).
 *  - The legacy door columns read those door attributes through a NESTED
 *    accessor keyed on the vertical (`cstore.*` / `lodging.*` for GooglePlace
 *    door tables, `restaurant.*` for the bespoke restaurant-doors table) and
 *    build the open-detail link from the door's own id inside that nested object.
 *  - SEPARATELY, the door-proxy-level columns read top-level `taglist`,
 *    `distributors`, `gpos`, `note_count`, `deals` (door-level) AND a sibling
 *    `chain_proxy` object (`chain_proxy.id` for row actions; `chain_proxy.*`
 *    campaigns/tags/distributors/gpos pill columns, hidden by default).
 *
 * So a single flat→nested copy under the accessor (the opps approach) is NOT
 * enough: it would also bury the door-level `taglist`/`distributors`/`gpos` the
 * common door-proxy columns read flat. {@link adaptUnifiedDoorItemForVertical}
 * therefore:
 *
 *  1. NESTS the whole flat row under the vertical accessor key, and sets the
 *     nested door-detail id (`<accessor>.id`) from `detail_id` so the legacy
 *     `<accessor>.id` link resolves. The nested copy carries the legacy door
 *     field names the accessor columns read — notably `address` (mirrored from
 *     `street_address`) and `address_info.zip` (mirrored from `zipcode`).
 *  2. KEEPS the door-level shared array columns (`taglist`, `distributors`,
 *     `gpos`) and `note_count` flat at the top level (door-proxy columns read
 *     them there), reshaping each `[{id,name}]` array column onto the cell-read
 *     key (`taglist` → `.tag`) via the shared `adaptUnifiedItem` reshape that
 *     fixed the TaglistCell crash.
 *  3. Sets the proxy ids the DECommonTable door row actions key off:
 *       - top-level `id` = the row's own `id` (the door-proxy id, mirrored from
 *         contacts_doorproxy; `doorProxyIdAccessorKey="id"`),
 *       - `chain_proxy.id` = `contact_company_id` (`chainProxyIdAccessorKey="chain_proxy.id"`).
 *
 * GAPS the unified door endpoint does not (yet) feed are left absent so the
 * legacy cells take their `?? '-'` / empty-pill fallbacks rather than crashing:
 * chain-level `chain_proxy.campaigns/taglist/distributors/gpos` and the
 * restaurant-doors V2 extras (`firstbite_id`, LTV, instagram, address_info
 * demographics, CRM-report rollups). These are tracked for browser QA.
 */

import { adaptUnifiedItem } from './unifiedResponseAdapter'

export type UnifiedDoorListParams = Record<string, unknown>

/**
 * How to re-nest a flat unified door row for one door-bearing vertical.
 *
 * Mirrors `VerticalRowConfig` but nests the door detail under the vertical
 * accessor and sets `id`/`chain_proxy.id` from the door-proxy / chain-proxy ids.
 *
 * Most door tables build their `/<linkPrefix>/<accessor>.id` detail link and key
 * selection off the nested `id`, but the bespoke restaurant-doors V2 table reads
 * the door-detail id off `restaurant.firstbite_id` instead (see
 * `DoorsTableColumnsV2.tsx` detail link and `DoorsTableComponentV2.tsx`
 * selection accessors). `detailLinkIdField` configures which nested key the
 * `detail_id` is written to so both shapes resolve.
 */
export interface DoorVerticalRowConfig {
  /** Detail-object accessor the legacy door columns nest under (`cstore`/`lodging`/`restaurant`). */
  accessor: string
  /**
   * Nested key the door-detail id (`detail_id`) is written to for the detail
   * link + selection. Defaults to `'id'` (cstore/lodging read `<accessor>.id`);
   * the restaurant-doors V2 table reads `restaurant.firstbite_id`.
   */
  detailLinkIdField?: string
}

/** Shared door fields whose flat unified name differs from the legacy nested name. */
const NESTED_FIELD_ALIASES: ReadonlyArray<readonly [from: string, to: string]> =
  [['street_address', 'address']]

/**
 * Re-nest one flat unified door row under its vertical accessor and set the
 * proxy ids the legacy door columns/links/row-actions read. See the file
 * docstring for the full contract.
 */
export function adaptUnifiedDoorItemForVertical<
  T extends Record<string, unknown>,
>(item: T, config: DoorVerticalRowConfig): T & Record<string, unknown> {
  // Reshape the door-level array columns (taglist → .tag, etc.) flat at top
  // level — the common door-proxy columns read them there.
  const base = adaptUnifiedItem(item)

  // FIR-7822: the chain-proxy id is the row's `contact_company_id` (door_crm_report
  // denormalizes the ContactCompany FK). `company_id` is the TENANT id, identical
  // across rows — sourcing chain_proxy.id from it collapsed every row to the
  // tenant id. The door-proxy id the row actions target is the row's own `id`
  // (door_crm_report.id, mirrored from contacts_doorproxy); `door_id` is the
  // unified_door id, NOT the door-proxy id.
  const chainProxyId = item['contact_company_id']
  const detailId = item['detail_id']
  const hasDetail = detailId !== null && detailId !== undefined

  // Nested door-detail object: the flat row's door attributes under the accessor
  // key, with the door-detail id and the legacy-named address fields the
  // `<accessor>.address` / `<accessor>.address_info.zip` cells read.
  const nested: Record<string, unknown> = { ...item }
  for (const [from, to] of NESTED_FIELD_ALIASES) {
    if (item[from] !== undefined && nested[to] === undefined) {
      nested[to] = item[from]
    }
  }
  if (item['zipcode'] !== undefined) {
    const addressInfo =
      typeof nested['address_info'] === 'object' && nested['address_info']
        ? (nested['address_info'] as Record<string, unknown>)
        : {}
    nested['address_info'] = { ...addressInfo, zip: item['zipcode'] }
  }
  // The door-detail id drives the open-detail link + selection. Most tables read
  // it off the nested `id`; the restaurant-doors V2 table reads it off
  // `restaurant.firstbite_id` (`detailLinkIdField`). When there is no backing
  // detail row, drop both the unified row's own `id` and the configured detail
  // field off the nested object so the link cell takes its empty fallback
  // instead of linking to a bogus id.
  const detailLinkIdField = config.detailLinkIdField ?? 'id'
  if (hasDetail) {
    nested['id'] = detailId
    nested[detailLinkIdField] = detailId
  } else {
    delete nested['id']
    delete nested[detailLinkIdField]
  }

  // Chain-proxy sibling: the row actions key off `chain_proxy.id` (the chain
  // proxy / ContactCompany id == unified `contact_company_id`). Chain-level pill
  // columns (campaigns/taglist/distributors/gpos) are not fed by the unified
  // endpoint and stay absent → empty pills (tracked for QA).
  const existingChainProxy =
    typeof base['chain_proxy'] === 'object' && base['chain_proxy']
      ? (base['chain_proxy'] as Record<string, unknown>)
      : {}
  // An unmatched door has no chain proxy (`contact_company_id` is null), so
  // `chain_proxy.id` is intentionally `undefined` here — there is no chain to
  // target, so chain-level actions no-op rather than hit a bogus id (the pre-fix
  // tenant `company_id` was always the wrong id for this slot). Door-level row
  // actions key off the top-level `id` (door-proxy id), not `chain_proxy.id`.
  const chainProxy: Record<string, unknown> = {
    ...existingChainProxy,
    id: chainProxyId ?? existingChainProxy['id'],
  }

  return {
    ...base,
    // Door-proxy id the row actions / selection target (`doorProxyIdAccessorKey="id"`).
    id: item['id'],
    chain_proxy: chainProxy,
    [config.accessor]: nested,
  } as T & Record<string, unknown>
}
